import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import { useTranslation } from "react-i18next";
import "./dateRangeFilter.scss";

// Filtro de rango de fechas estilo mockup: chips de presets rápidos + un
// desplegable "Más" con todos los presets y un rango personalizado.
// onChange entrega (startYMD, endYMD, preset) en formato "YYYY-MM-DD".
//
// Audit #7: este componente es la versión canónica. Los presets son
// configurables vía la prop `presets` para que consumidores como
// deliveryQuality (60 días, Este mes hasta hoy) puedan usar otros.

export function rangeFromPreset(preset) {
  const t = moment();
  switch (preset) {
    case "today":
      return [t.clone().startOf("day"), t.clone().endOf("day")];
    case "yesterday": {
      const y = t.clone().subtract(1, "day");
      return [y.clone().startOf("day"), y.clone().endOf("day")];
    }
    case "7d":
      return [t.clone().subtract(6, "days").startOf("day"), t.clone().endOf("day")];
    case "30d":
      return [t.clone().subtract(29, "days").startOf("day"), t.clone().endOf("day")];
    case "60d":
      return [t.clone().subtract(59, "days").startOf("day"), t.clone().endOf("day")];
    case "90d":
      return [t.clone().subtract(89, "days").startOf("day"), t.clone().endOf("day")];
    case "thisWeek":
      // Semana en curso: lunes (inicio de semana ISO, independiente del locale)
      // hasta el final de hoy. "lunes→hoy".
      return [t.clone().startOf("isoWeek"), t.clone().endOf("day")];
    case "thisMonth":
      return [t.clone().startOf("month"), t.clone().endOf("month")];
    case "thisMonthToToday":
      return [t.clone().startOf("month"), t.clone().endOf("day")];
    case "lastMonth": {
      const p = t.clone().subtract(1, "month");
      return [p.clone().startOf("month"), p.clone().endOf("month")];
    }
    default:
      return [t.clone().startOf("day"), t.clone().endOf("day")];
  }
}

// DEFAULT_PRESETS — set canónico expuesto para consumidores que quieran
// extender o reemplazar la lista. Cada entry es { key, quick }:
//  - key: identificador (también usado como `value` y por rangeFromPreset)
//  - quick: si aparece como chip rápido (true) o solo dentro del popover.
// Los labels los resuelve PRESET_LABELS_T (i18n vía useTranslation).
export const DEFAULT_PRESETS = [
  { key: "today", quick: true },
  { key: "yesterday", quick: true },
  { key: "7d", quick: true },
  { key: "30d", quick: true },
  { key: "90d", quick: false },
  { key: "thisMonth", quick: true },
  { key: "lastMonth", quick: false },
];

// Formatea una fecha "YYYY-MM-DD" a un texto corto en español (ej. "27 abr 2026").
// Usa moment (ya disponible en el repo) con locale "es".
function formatRangeDate(ymd) {
  if (!ymd) return "";
  return moment(ymd, "YYYY-MM-DD").locale("es").format("D MMM YYYY");
}

// customRange (opcional): { start, end } en "YYYY-MM-DD". Si se entrega, el
// resumen inferior muestra ESE rango concreto en lugar del derivado del preset.
// Lo usa el consumidor que controla el rango por fuera (p.ej. ampliación
// incremental del estado vacío en Admin/pedidos). Es retrocompatible: si no se
// pasa, el componente sigue resolviendo el rango desde value como antes.
//
// presets (opcional): array de { key, quick } para sobreescribir DEFAULT_PRESETS.
// Para añadir un preset nuevo (60d, thisWeek, thisMonthToToday) basta con
// incluirlo aquí — rangeFromPreset y PRESET_LABELS_T ya conocen todas las keys
// del repo. Ejemplo para sumar "Esta semana" sin tocar el resto:
//   presets={[...DEFAULT_PRESETS, { key: "thisWeek", quick: true }]}
// (thisWeek NO se incluye en DEFAULT_PRESETS para no alterar a los consumidores
// actuales; es opt-in).
//
// compact (opcional): variante embebible. Por defecto (false) el componente se
// ve EXACTAMENTE igual que siempre (inline, alineado a la derecha, full-width en
// el header). En true, el wrapper pasa a ocupar el ancho del contenedor con
// chips/popover más compactos y el resumen alineado a la izquierda, pensado para
// modales de filtros, drawers y pasos de wizard. Solo presentación.
//
// className (opcional): clases extra que se anexan al wrapper raíz para casos de
// inserción puntuales (márgenes, ancho, etc.) sin tocar el SCSS del componente.
export default function DateRangeFilter({
  value = "7d",
  onChange,
  onClear,
  customRange = null,
  presets = DEFAULT_PRESETS,
  compact = false,
  className = "",
}) {
  const { t } = useTranslation();
  const PRESET_LABELS_T = {
    today: t("Hoy"),
    yesterday: t("Ayer"),
    "7d": t("7 días"),
    "30d": t("30 días"),
    "60d": t("60 días"),
    "90d": t("90 días"),
    thisWeek: t("Esta semana"),
    thisMonth: t("Este mes"),
    thisMonthToToday: t("Este mes hasta hoy"),
    lastMonth: t("Mes anterior"),
    custom: t("Personalizado"),
  };
  // Derivamos QUICK/ALL desde la prop presets (memoizado por referencia simple).
  const QUICK = presets.filter((p) => p.quick).map((p) => p.key);
  const ALL = presets.map((p) => p.key);
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  // Última fecha custom aplicada (para poder mostrarla abajo en modo "custom").
  const [appliedCustom, setAppliedCustom] = useState({ from: "", to: "" });
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const applyPreset = (p) => {
    const [start, end] = rangeFromPreset(p);
    onChange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"), p);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setAppliedCustom({ from: customFrom, to: customTo });
    onChange(customFrom, customTo, "custom");
    setOpen(false);
  };

  // "Más" queda activo cuando el preset elegido no está entre los rápidos.
  const moreActive = !QUICK.includes(value);

  // Resuelve el rango concreto (start/end en "YYYY-MM-DD") del preset/custom
  // actual para mostrarlo en fechas concretas en la parte inferior (5.1).
  const resolvedRange = (() => {
    // Rango controlado por el consumidor (ampliación incremental u otros): tiene
    // prioridad sobre cualquier derivación interna.
    if (customRange && customRange.start && customRange.end) {
      return { start: customRange.start, end: customRange.end };
    }
    if (value === "custom") {
      if (appliedCustom.from && appliedCustom.to) {
        return { start: appliedCustom.from, end: appliedCustom.to };
      }
      return null; // custom sin rango aplicado todavía
    }
    // value que no es un preset conocido (p.ej. "" cuando el consumidor quitó
    // el filtro de fecha) → sin resumen.
    if (!ALL.includes(value)) return null;
    const [start, end] = rangeFromPreset(value);
    return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
  })();

  const handleClear = () => {
    setAppliedCustom({ from: "", to: "" });
    setCustomFrom("");
    setCustomTo("");
    setOpen(false);
    if (typeof onClear === "function") onClear();
  };

  // Wrapper class: base + variante compacta (opt-in) + passthrough del consumidor.
  // Por defecto queda exactamente "date-range-filter-wrapper" como siempre.
  const wrapperClassName = [
    "date-range-filter-wrapper",
    compact ? "date-range-filter-wrapper--compact" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClassName}>
      <div className="date-range-filter">
      {QUICK.map((p) => (
        <button
          key={p}
          type="button"
          className={`drf-chip ${value === p ? "drf-chip--active" : ""}`}
          onClick={() => applyPreset(p)}
        >
          {PRESET_LABELS_T[p]}
        </button>
      ))}

      <div className="drf-more" ref={ref}>
        <button
          type="button"
          className={`drf-chip drf-chip--more ${moreActive ? "drf-chip--active" : ""}`}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="true"
          aria-expanded={open}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          {moreActive ? PRESET_LABELS_T[value] : t("Rango")}
          <span className="material-symbols-outlined drf-chip__chevron">expand_more</span>
        </button>

        {open && (
          <div className="drf-popover">
            <div className="drf-popover__label">{t("Rangos rápidos")}</div>
            <div className="drf-popover__grid">
              {ALL.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`drf-preset ${value === p ? "drf-preset--active" : ""}`}
                  onClick={() => applyPreset(p)}
                >
                  {PRESET_LABELS_T[p]}
                </button>
              ))}
            </div>
            <div className="drf-popover__custom">
              <div className="drf-popover__label">{t("Personalizado")}</div>
              <div className="drf-custom-row">
                <label className="drf-custom-field">
                  <span>{t("Desde")}</span>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </label>
                <label className="drf-custom-field">
                  <span>{t("Hasta")}</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </label>
              </div>
              <button
                type="button"
                className="drf-apply"
                disabled={!customFrom || !customTo}
                onClick={applyCustom}
              >
                {t("Aplicar rango")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

      {/* Rango concreto actualmente seleccionado, en fechas legibles (5.1).
          Si se pasa onClear, se muestra una "X" para eliminar el filtro (5.3a). */}
      {resolvedRange && (
        <div className="drf-summary">
          <span className="material-symbols-outlined drf-summary__icon">date_range</span>
          <span className="drf-summary__text">
            {t("del")} {formatRangeDate(resolvedRange.start)} {t("al")}{" "}
            {formatRangeDate(resolvedRange.end)}
          </span>
          {typeof onClear === "function" && (
            <button
              type="button"
              className="drf-summary__clear"
              onClick={handleClear}
              aria-label={t("Quitar filtro de fecha")}
              title={t("Quitar filtro de fecha")}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
